import { useContext, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import TenantContext from "../../context/tenant/TenantContext";
import UploadDocumentBox from "./UploadDocumentsCards";
import { toast } from "../../helper/common";

const TenantChecksUploadDocument = ({
  formHandler,
  user,
  updateUserProfile,
  additionalInfo,
  getNextStep,
}) => {
  const { _t } = useTranslator();
  const { uploadFileData } = useContext(TenantContext);

  const [tenantFiles, setTenantFiles] = useState([]);
  const [guarantorFiles, setGuarantorFiles] = useState([]);

  const [nextDisable, setNextDisable] = useState(false);
  const [tenantDocUploadLoading, setTenantDocUploadLoading] = useState(false);
  const [guarantorDocUploadLoading, setGuarantorDocUploadLoading] =
    useState(false);
  const [tenantInvalidFiles, setTenantInvalidFiles] = useState([]);
  const [guarantorInvalidFiles, setGuarantorInvalidFiles] = useState([]);

  const handleUploadFiles = async (
    id,
    files,
    setDocumentLoading,
    setFiles,
    fileType = "uploaded-from-onboarding",
    fileFrom
  ) => {
    if (!id) return;
    try {
      setDocumentLoading(true);
      const formData = new FormData();
      for (let file of files) {
        formData.append("tenantDocs", file);
      }
      formData.append("fileType", fileType);
      if (fileFrom) formData.append("fileFrom", fileFrom);

      await uploadFileData(formData, true, id, false);
      setFiles([]);
    } catch (err) {
      console.error("Error uploading multiple files:", err);
    } finally {
      setDocumentLoading(false);
    }
  };
  const handleInitiateUpload = async () => {
    try {
      setNextDisable(true);

      const isTenantDocsUploaded = user?.docs_uploaded;
      const isGuarantorDocsUploaded = user?.gurantor_id?.[0]?.docs_uploaded;

      const needTenantUpload = tenantFiles.length > 0;
      const needGuarantorUpload =
        guarantorFiles.length > 0 &&
        additionalInfo?.includes?.("TENANT_GUARANTOR");

      // ❌ If no files and nothing uploaded, block next step
      if (
        (!isTenantDocsUploaded && tenantFiles.length === 0) ||
        (additionalInfo?.includes?.("TENANT_GUARANTOR") &&
          !isGuarantorDocsUploaded &&
          guarantorFiles.length === 0)
      ) {
        toast.error(_t("pleaseUploadDocumentsToContinueTenantChecks"));
        setNextDisable(false);
        return;
      }

      // ✅ Update user profile step
      const step = getNextStep();
      await updateUserProfile({ id: user?._id, step });

      // ✅ Only upload if files exist (skip Promise.all if nothing to upload)
      const uploadPromises = [];
      if (needTenantUpload) {
        uploadPromises.push(
          handleUploadFiles(
            user?._id,
            tenantFiles,
            setTenantDocUploadLoading,
            setTenantFiles
          )
        );
      }
      if (needGuarantorUpload) {
        uploadPromises.push(
          handleUploadFiles(
            user?.gurantor_id?.[0]?._id,
            guarantorFiles,
            setGuarantorDocUploadLoading,
            setGuarantorFiles
          )
        );
      }

      if (uploadPromises.length > 0) {
        await Promise.all(uploadPromises);
      }

      // ✅ Continue to next step
      setNextDisable(false);
    } catch (err) {
      console.error(err);
      setNextDisable(false);
    }
  };

  return (
    <div className="popup-overlay fixed inset-0 flex items-center justify-center z-50 wrapper-modal modal-fixed-screen guarantorBg">
      <div
        className={`popup-content2 position-relative popup-content bg-white required-doc-popup onfiddoModal uploadDocumentsChecks ${
          additionalInfo?.includes?.("TENANT_GUARANTOR") > 0
            ? "uploadDocumentsBoth"
            : ""
        }`}
      >
        <h2
          className={`completeTenantChecks ${
            additionalInfo?.includes?.("TENANT_GUARANTOR") > 0
              ? ""
              : "text-center"
          }`}
        >
          {additionalInfo?.includes?.("TENANT_GUARANTOR") > 0
            ? _t("uploadDocuments")
            : _t("uploadTenantDocuments")}
        </h2>
        <div className="uploadDocumentBoxWrapper d-flex align-items-center justify-content-center">
          <UploadDocumentBox
            _t={_t}
            title={
              additionalInfo?.includes?.("TENANT_GUARANTOR") > 0
                ? _t("tenantDocuments")
                : ""
            }
            files={tenantFiles}
            setFiles={setTenantFiles}
            loading={tenantDocUploadLoading}
            setLoading={setTenantDocUploadLoading}
            setGlobalInvalidFiles={setTenantInvalidFiles}
          />

          {additionalInfo?.includes?.("TENANT_GUARANTOR") > 0 && (
            <UploadDocumentBox
              _t={_t}
              title={_t("guarantorDocuments")}
              files={guarantorFiles}
              setFiles={setGuarantorFiles}
              loading={guarantorDocUploadLoading}
              setLoading={setGuarantorDocUploadLoading}
              setGlobalInvalidFiles={setGuarantorInvalidFiles}
            />
          )}
        </div>

        <div className="d-flex align-items-center justify-content-center gap-3">
          <button
            type="button"
            className="btnNext btnnextpre"
            onClick={formHandler.prev}
          >
            {_t("back")}
          </button>
          <button
            type="button"
            onClick={() => handleInitiateUpload()}
            disabled={
              nextDisable ||
              tenantInvalidFiles.length > 0 ||
              guarantorInvalidFiles.length > 0
            }
            className="btnNext greenHover"
          >
            {_t("next")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantChecksUploadDocument;
